import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import BackBar from '../components/BackBar';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  
  // Selection state for bulk delete
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [deleting, setDeleting] = useState(false);

  // Pricing state
  const [pricing, setPricing] = useState(null);
  const [editingPricing, setEditingPricing] = useState(null);
  const [editingAddons, setEditingAddons] = useState(null);
  const [pricingSaving, setPricingSaving] = useState(false);

  // Filters for projects
  const [stateFilter, setStateFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsRes, usersRes, projectsRes, pricingRes] = await Promise.all([
        axios.get(`${API}/api/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/admin/projects?limit=100`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/admin/pricing`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setProjects(projectsRes.data.projects);
      setPricing(pricingRes.data);
      setEditingPricing(pricingRes.data.prices);
      setEditingAddons(pricingRes.data.addons || {});
      setSelectedProjects([]); // Clear selection on reload
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  // Toggle project selection
  const toggleProjectSelection = (projectId) => {
    setSelectedProjects(prev => 
      prev.includes(projectId) 
        ? prev.filter(id => id !== projectId)
        : [...prev, projectId]
    );
  };

  // Select/deselect all filtered projects
  const toggleSelectAll = () => {
    const filteredIds = filteredProjects.map(p => p.id);
    const allSelected = filteredIds.every(id => selectedProjects.includes(id));
    
    if (allSelected) {
      setSelectedProjects(prev => prev.filter(id => !filteredIds.includes(id)));
    } else {
      setSelectedProjects(prev => [...new Set([...prev, ...filteredIds])]);
    }
  };

  // Delete selected projects
  const deleteSelectedProjects = async () => {
    if (selectedProjects.length === 0) return;
    
    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedProjects.length} project(s)? This action cannot be undone.`
    );
    
    if (!confirmDelete) return;
    
    setDeleting(true);
    try {
      await Promise.all(
        selectedProjects.map(projectId =>
          axios.delete(`${API}/api/admin/projects/${projectId}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        )
      );
      setSelectedProjects([]);
      loadData(); // Refresh data
    } catch (err) {
      alert('Failed to delete some projects. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const savePricing = async () => {
    try {
      setPricingSaving(true);
      await axios.put(
        `${API}/api/admin/pricing`,
        { prices: editingPricing, addons: editingAddons },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refresh pricing
      const pricingRes = await axios.get(`${API}/api/admin/pricing`, { headers: { Authorization: `Bearer ${token}` } });
      setPricing(pricingRes.data);
      setEditingPricing(pricingRes.data.prices);
      setEditingAddons(pricingRes.data.addons || {});
      alert('Pricing updated successfully!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update pricing');
    } finally {
      setPricingSaving(false);
    }
  };

  const updatePrice = (planType, devType, value) => {
    const numValue = parseInt(value) || 0;
    setEditingPricing(prev => ({
      ...prev,
      [planType]: {
        ...prev[planType],
        [devType]: numValue
      }
    }));
  };

  const updateAddonPrice = (addonKey, value) => {
    const numValue = parseInt(value) || 0;
    setEditingAddons(prev => ({
      ...prev,
      [addonKey]: {
        ...prev[addonKey],
        price: numValue
      }
    }));
  };

  const toggleUserDisabled = async (userId) => {
    try {
      setActionLoading(userId);
      await axios.put(
        `${API}/api/admin/users/${userId}/toggle-disable`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refresh users
      const usersRes = await axios.get(`${API}/api/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(usersRes.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update user');
    } finally {
      setActionLoading(null);
    }
  };

  const downloadWord = async (projectId, projectName) => {
    try {
      setActionLoading(projectId);
      const response = await axios.get(
        `${API}/api/admin/projects/${projectId}/download-word`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${projectName.replace(/\s+/g, '_')}_editable.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Failed to download Word document');
    } finally {
      setActionLoading(null);
    }
  };

  const downloadPDF = async (projectId, projectName, planType) => {
    try {
      setActionLoading(projectId);
      // Use the correct endpoint based on plan type
      const endpoint = planType === 'demolition_construction_wmp' 
        ? `${API}/api/projects/${projectId}/dcwmp/download-pdf`
        : `${API}/api/projects/${projectId}/download-pdf`;
      
      const response = await axios.get(endpoint, { 
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const prefix = planType === 'demolition_construction_wmp' ? 'DCWMP' : 'OpWMP';
      link.setAttribute('download', `${prefix}_${projectName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Failed to download PDF. Project may be incomplete.');
    } finally {
      setActionLoading(null);
    }
  };

  const filteredProjects = projects.filter(p => {
    if (stateFilter && p.state_code !== stateFilter) return false;
    if (paymentFilter && p.payment_status !== paymentFilter) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <BackBar to="/admin" label="Admin" />

      <div className="max-w-7xl mx-auto px-4 py-6">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex gap-4">
            {['overview', 'users', 'projects', 'pricing'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-3 px-1 border-b-2 font-medium text-sm capitalize ${
                  activeTab === tab
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg shadow p-6">
                <p className="text-sm text-gray-500">Total Users</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total_users}</p>
                <p className="text-xs text-gray-400 mt-1">{stats.disabled_users} disabled</p>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <p className="text-sm text-gray-500">Total Projects</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total_projects}</p>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <p className="text-sm text-gray-500">Paid Projects</p>
                <p className="text-3xl font-bold text-green-600">{stats.paid_projects}</p>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <p className="text-sm text-gray-500">Unpaid Projects</p>
                <p className="text-3xl font-bold text-amber-600">{stats.unpaid_projects}</p>
              </div>
            </div>

            {/* Projects by State */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Projects by State</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {stats.projects_by_state.map(item => (
                  <div key={item.state} className="bg-gray-50 rounded p-3">
                    <p className="text-sm font-medium text-gray-600">{item.state}</p>
                    <p className="text-2xl font-bold text-gray-900">{item.count}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map(u => (
                  <tr key={u.id} className={u.is_disabled ? 'bg-red-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{u.full_name}</span>
                        {u.is_admin && (
                          <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">Admin</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <a 
                          href={`mailto:${u.email}`} 
                          className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                          title="Send email"
                        >
                          {u.email}
                        </a>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(u.email);
                            alert('Email copied to clipboard');
                          }}
                          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                          title="Copy email"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded ${
                        u.is_disabled 
                          ? 'bg-red-100 text-red-700' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {u.is_disabled ? 'Disabled' : 'Active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {!u.is_admin && (
                        <button
                          onClick={() => toggleUserDisabled(u.id)}
                          disabled={actionLoading === u.id}
                          className={`text-sm px-3 py-1 rounded ${
                            u.is_disabled
                              ? 'bg-green-600 hover:bg-green-700 text-white'
                              : 'bg-red-600 hover:bg-red-700 text-white'
                          } disabled:opacity-50`}
                        >
                          {actionLoading === u.id ? '...' : (u.is_disabled ? 'Enable' : 'Disable')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <div className="space-y-4">
            {/* Filters and Actions */}
            <div className="flex flex-wrap gap-4 bg-white rounded-lg shadow p-4 items-center justify-between">
              <div className="flex gap-4 items-center">
                <select
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  data-testid="state-filter"
                >
                  <option value="">All States</option>
                  <option value="VIC">VIC</option>
                  <option value="NSW">NSW</option>
                  <option value="QLD">QLD</option>
                  <option value="WA">WA</option>
                  <option value="SA">SA</option>
                  <option value="TAS">TAS</option>
                  <option value="ACT">ACT</option>
                </select>
                <select
                  value={paymentFilter}
                  onChange={(e) => setPaymentFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  data-testid="payment-filter"
                >
                  <option value="">All Payment Status</option>
                  <option value="paid">Paid</option>
                  <option value="pending">Pending</option>
                </select>
                <span className="text-sm text-gray-500">
                  Showing {filteredProjects.length} of {projects.length} projects
                </span>
              </div>
              
              {/* Delete Selected Button */}
              {selectedProjects.length > 0 && (
                <button
                  onClick={deleteSelectedProjects}
                  disabled={deleting}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                  data-testid="delete-selected-btn"
                >
                  {deleting ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete Selected ({selectedProjects.length})
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Projects Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={filteredProjects.length > 0 && filteredProjects.every(p => selectedProjects.includes(p.id))}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">State</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Add-ons</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredProjects.map(p => (
                    <tr key={p.id} className={`${p.bundle_id ? 'bg-purple-50' : ''} ${selectedProjects.includes(p.id) ? 'bg-blue-50' : ''}`}>
                      <td className="px-3 py-4">
                        <input
                          type="checkbox"
                          checked={selectedProjects.includes(p.id)}
                          onChange={() => toggleProjectSelection(p.id)}
                          className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                          data-testid={`project-checkbox-${p.id}`}
                        />
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {p.project_name}
                          {p.bundle_id && (
                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                              Bundle
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">{p.site_address}</div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">
                        {p.plan_type === 'demolition_construction_wmp' ? 'D&CWMP' : 'OpWMP'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">{p.state_code}</td>
                      <td className="px-4 py-4">
                        {p.selected_addons && p.selected_addons.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {p.selected_addons.map(addon => {
                              // Map addon keys to display names
                              const addonNames = {
                                'compliance_check': 'Compliance Review',
                                'rfi_response': 'RFI Response',
                                'extra_30_day': '30 Day Access',
                                'bin_storage_review': 'Storage Review'
                              };
                              return (
                                <span key={addon} className="px-1.5 py-0.5 text-xs rounded bg-blue-100 text-blue-700">
                                  {addonNames[addon] || addon.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">None</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-1 text-xs rounded ${
                          p.payment_status === 'paid'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {p.payment_status || 'pending'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">
                        {new Date(p.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-4">
                        <button
                          onClick={() => downloadPDF(p.id, p.project_name, p.plan_type)}
                          disabled={actionLoading === p.id}
                          className="text-sm px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50"
                        >
                          {actionLoading === p.id ? '...' : 'PDF'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pricing Tab */}
        {activeTab === 'pricing' && editingPricing && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Pricing Configuration</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Manage prices for all plan types and development types (AUD, excluding GST)
                  </p>
                </div>
                <button
                  onClick={savePricing}
                  disabled={pricingSaving}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium disabled:opacity-50"
                >
                  {pricingSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>

              {/* Pricing Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Plan Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Residential
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Commercial
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Mixed Use
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {/* OpWMP Row */}
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">Operational WMP</div>
                        <div className="text-xs text-gray-500">OpWMP</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={editingPricing.opwmp?.Residential || 0}
                            onChange={(e) => updatePrice('opwmp', 'Residential', e.target.value)}
                            className="w-28 pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={editingPricing.opwmp?.Commercial || 0}
                            onChange={(e) => updatePrice('opwmp', 'Commercial', e.target.value)}
                            className="w-28 pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={editingPricing.opwmp?.['Mixed use'] || 0}
                            onChange={(e) => updatePrice('opwmp', 'Mixed use', e.target.value)}
                            className="w-28 pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </td>
                    </tr>

                    {/* D&CWMP Row */}
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">Demolition & Construction WMP</div>
                        <div className="text-xs text-gray-500">D&CWMP</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={editingPricing.dcwmp?.Residential || 0}
                            onChange={(e) => updatePrice('dcwmp', 'Residential', e.target.value)}
                            className="w-28 pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={editingPricing.dcwmp?.Commercial || 0}
                            onChange={(e) => updatePrice('dcwmp', 'Commercial', e.target.value)}
                            className="w-28 pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={editingPricing.dcwmp?.['Mixed use'] || 0}
                            onChange={(e) => updatePrice('dcwmp', 'Mixed use', e.target.value)}
                            className="w-28 pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </td>
                    </tr>

                    {/* CMP Row */}
                    <tr className="bg-indigo-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-indigo-900">Construction Management Plan</div>
                        <div className="text-xs text-indigo-600">CMP</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={editingPricing.cmp?.Residential || 0}
                            onChange={(e) => updatePrice('cmp', 'Residential', e.target.value)}
                            className="w-28 pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={editingPricing.cmp?.Commercial || 0}
                            onChange={(e) => updatePrice('cmp', 'Commercial', e.target.value)}
                            className="w-28 pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={editingPricing.cmp?.['Mixed use'] || 0}
                            onChange={(e) => updatePrice('cmp', 'Mixed use', e.target.value)}
                            className="w-28 pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                      </td>
                    </tr>

                    {/* Bundled Row */}
                    <tr className="bg-blue-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-blue-900">Bundled Pack</div>
                        <div className="text-xs text-blue-600">OpWMP + D&CWMP</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={editingPricing.bundled?.Residential || 0}
                            onChange={(e) => updatePrice('bundled', 'Residential', e.target.value)}
                            className="w-28 pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div className="text-xs text-green-600 mt-1">
                          Save ${((editingPricing.opwmp?.Residential || 0) + (editingPricing.dcwmp?.Residential || 0)) - (editingPricing.bundled?.Residential || 0)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={editingPricing.bundled?.Commercial || 0}
                            onChange={(e) => updatePrice('bundled', 'Commercial', e.target.value)}
                            className="w-28 pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div className="text-xs text-green-600 mt-1">
                          Save ${((editingPricing.opwmp?.Commercial || 0) + (editingPricing.dcwmp?.Commercial || 0)) - (editingPricing.bundled?.Commercial || 0)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={editingPricing.bundled?.['Mixed use'] || 0}
                            onChange={(e) => updatePrice('bundled', 'Mixed use', e.target.value)}
                            className="w-28 pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div className="text-xs text-green-600 mt-1">
                          Save ${((editingPricing.opwmp?.['Mixed use'] || 0) + (editingPricing.dcwmp?.['Mixed use'] || 0)) - (editingPricing.bundled?.['Mixed use'] || 0)}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Pricing Info */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Pricing Notes</h4>
                <ul className="text-xs text-gray-500 space-y-1">
                  <li>• All prices are in AUD and exclude GST (10% GST will be added at checkout)</li>
                  <li>• Bundled Pack pricing should be less than the sum of OpWMP + D&CWMP for the same development type</li>
                  <li>• Price changes take effect immediately for new projects</li>
                </ul>
              </div>
            </div>

            {/* Add-on Services Section */}
            {editingAddons && Object.keys(editingAddons).length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Add-on Services</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Manage prices for optional add-on services (AUD, excluding GST)
                  </p>
                </div>

                <div className="grid gap-4">
                  {Object.entries(editingAddons).map(([key, addon]) => (
                    <div key={key} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{addon.name}</div>
                        <div className="text-sm text-gray-500">{addon.description}</div>
                      </div>
                      <div className="relative ml-4">
                        <span className="absolute left-3 top-2 text-gray-500">$</span>
                        <input
                          type="number"
                          value={addon.price || 0}
                          onChange={(e) => updateAddonPrice(key, e.target.value)}
                          className="w-28 pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
